/**
 * 版面的**結構性**切換（批171）：手機／平板（<1200）時側欄整個換成頂欄＋漢堡抽屜。
 *
 * 那不是「窄一點」而是**不同的 DOM**（抽屜要有遮罩、要能點外面關、換頁要自己收），純 CSS
 * `@media` 蓋不掉，所以做成 JS 條件化（與 CS 工作台、LS portal 的 `lib/viewport.ts` 同一套做法）。
 * 查詢字串一律取自 `lib/breakpoints.ts`（CS 正本的副本），CSS 那邊的 `@media (--compact)` 是同一條。
 *
 * ★ 這裡**不引 `@vueuse/core`**：後台的 dependencies 只有 vue／vue-router／pinia 三個，
 *   為了一個 `useMediaQuery` 拉一整包不划算。手寫版行為等價（`matchMedia` + `change` 監聽 +
 *   模組級 singleton，全 app 共用一個 listener）；寫法照 `portal/src/lib/viewport.ts`。
 *
 * 純樣式的微調（padding、字級、單欄化）仍然寫各元件 scoped 的 `@media (--mobile)`／`(--compact)`，
 * 不要為了那種事 import 這支。
 */
import { computed, readonly, ref, type Ref } from 'vue'

import { MEDIA } from './breakpoints'

/** 建一個跟著 matchMedia 即時更新的唯讀旗標（模組級 singleton 用）。 */
function mediaFlag(query: string): Readonly<Ref<boolean>> {
  const state = ref(false)
  // SSR／測試環境（jsdom）可能沒有 matchMedia——沒有就當桌機，別讓整個 app 掛掉。
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mql = window.matchMedia(query)
    state.value = mql.matches
    const onChange = (e: MediaQueryListEvent) => {
      state.value = e.matches
    }
    // Safari < 14 只有 addListener；兩種都試（沒有 addEventListener 的環境不會有 change 事件）。
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange)
    else if (typeof mql.addListener === 'function') mql.addListener(onChange)
  }
  return readonly(state)
}

/** 手機版面（直向 ≤768，或手機橫放）。CSS 對應 `@media (--mobile)`。 */
export const isMobile = mediaFlag(MEDIA.mobile)

/** 手機＋平板（<1200）：後台側欄改成頂欄＋漢堡抽屜。CSS 對應 `@media (--compact)`。 */
export const isCompact = mediaFlag(MEDIA.compact)

/** 平板（769～1199、而且不是手機橫放）。 */
export const isTablet = computed(() => isCompact.value && !isMobile.value)

/**
 * 滑鼠類指標（可懸停＋精準）。觸控裝置是 false——說明文字要寫「長按」不寫鍵盤快捷鍵（批171 通則：
 * 觸控裝置沒有 Ctrl／Esc）。與 CS 工作台 `lib/viewport.ts` 的同名旗標同一條查詢。
 */
export const isHoverPointer = mediaFlag('(hover: hover) and (pointer: fine)')
